import { styled } from '@mui/material';
import { Send } from '@mui/icons-material';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useI18n } from '@app-velocity/i18n';
import { Form, Skeleton, TextField } from '@app-velocity/ui';
import { ClassValidatorResolver } from '@app-velocity/validators';

import { getDiscussionAnswer } from '@app-velocity/common';

import { QuestionDto } from '../../dto/QuestionDto';
import { useDiscussionContext } from '../../hooks/useDiscussionContext';
import { DiscussionProps } from '../../types';
import { History } from '../History';

const StyledSendButton = styled('button')({
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
  display: 'flex',
  alignItems: 'center',
  color: 'var(--text-text-secondary-color)',
  '&:disabled': {
    cursor: 'default',
    opacity: 0.5,
  },
});

export type DiscussionContentProps = Omit<DiscussionProps, 'hidden'>;

export const DiscussionContent = ({
  challenge,
  takenAction,
  element,
  explanation,
}: DiscussionContentProps) => {
  const { t } = useI18n();
  const [awaitingReply, setAwaitingReply] = useState(false);
  const {
    messages,
    isLoadingMessages,
    isSending,
    sendQuestion,
  } = useDiscussionContext();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<QuestionDto>({
    mode: 'onSubmit',
    resolver: ClassValidatorResolver(QuestionDto),
  });

  const lastMessageIsUser = useMemo(() => {
    if (!messages?.length) return false;

    return messages[messages.length - 1]?.messageType === 'USER';
  }, [messages]);

  useEffect(() => {
    if (lastMessageIsUser) {
      setAwaitingReply(true);

      return;
    }

    if (messages?.length) {
      setAwaitingReply(false);
    }
  }, [lastMessageIsUser, messages?.length]);

  const getPreviousDiscussion = () => {
    if (!messages || messages.length === 0) return '';

    const lastMessages = messages.slice(-6);

    return `Previous questions and your answers:
        ${lastMessages.map((item) =>
          item.messageType === 'USER'
            ? `User: ${item.originalMessage}.\n`
            : `You: ${getDiscussionAnswer(item.content || '')}.\n`
        )}`;
  };

  const submitQuestion = async ({ question }: QuestionDto) => {
    reset({ question: '' });
    setAwaitingReply(true);

    try {
      await sendQuestion({
        question,
        challenge,
        takenAction,
        element,
        explanation,
        discussion: getPreviousDiscussion(),
      });
    } catch {
      setAwaitingReply(false);
    }
  };

  const showPendingState = isSending || isLoadingMessages || awaitingReply;

  return (
    <>
      {messages ? <History messages={messages} /> : null}
      {showPendingState ? (
        <>
          {!lastMessageIsUser ? (
            <Skeleton height={60} margin="0 0 8px 32px" width="auto" />
          ) : null}
          <Skeleton height={60} margin="0 32px 8px 0" width="auto" />
        </>
      ) : null}
      <Form onSubmit={handleSubmit(submitQuestion)}>
        <Form.FieldContainer>
          <TextField
            name="question"
            control={control}
            fullWidth
            error={!!errors.question}
            helperText={errors.question?.message}
            placeholder={t('prototypes:question_placeholder')}
            disabled={showPendingState}
            endAdornment={
              <StyledSendButton
                type="submit"
                disabled={showPendingState}
                aria-label={t('prototypes:send_question')}
              >
                <Send fontSize="small" />
              </StyledSendButton>
            }
          />
        </Form.FieldContainer>
      </Form>
    </>
  );
};
